// Hook that abstracts the timing issues around requestAnimationFrame,
// and cancels the most recent request on cleanup.

// A lot of frame requests are granted per second; so avoid becoming very slow,
// most of them should return immediately. We pass a "framesPerSecond" parameter
// that makes sure the function is only run if 1s / framesPerSecond has elapsed since
// the last time it started. Its default is 10.

import { useCallback, useEffect, useRef } from "react";

export function useAnimationFrame(callback: () => void, framesPerSecond = 10) {
  // Keep the previous animation timestamp.
  const previousTimestamp = useRef<number>(0);

  // This holds the animation request reference, so it can be canceled.
  const animationRequestRef = useRef<number>(0);

  const wholeCallback = useCallback(
    (timestampMs: number) => {
      // Timing
      if (timestampMs === previousTimestamp.current) {
        // When multiple animation requests are going on, they all get the same time.
        // Skip this one.
        return;
      }

      const delta = timestampMs - previousTimestamp.current;
      if (delta < 1000 / framesPerSecond) {
        // Too soon. Do a new request.
        animationRequestRef.current = requestAnimationFrame(wholeCallback);
        return;
      }

      // The actual work happens here.
      previousTimestamp.current = timestampMs;
      callback();
      animationRequestRef.current = requestAnimationFrame(wholeCallback);
    },
    [callback, framesPerSecond]
  );

  useEffect(() => {
    // Do the first call
    animationRequestRef.current = requestAnimationFrame(wholeCallback);
    // Return callback that stops the animation at the end of this hook's lifecycle.
    return () => cancelAnimationFrame(animationRequestRef.current);
  }, [wholeCallback]);
}
